import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';

import SafeImage from './SafeImage';

import './PreviewThumb.scss';

const OPEN_DELAY_MS = 100;
const CARD_WIDTH = 320;
const CARD_HEIGHT = 384;
const GAP = 8;

// The card is position: fixed on document.body, so it escapes the scrolling list; it flips to the
// left of the thumb when the right edge would leave the viewport.
const getCardAnchor = rect => {
  const fitsRight = rect.right + GAP + CARD_WIDTH <= window.innerWidth;
  return {
    side: fitsRight ? 'right' : 'left',
    left: fitsRight ? rect.right + GAP : rect.left - GAP - CARD_WIDTH,
    top: _.clamp(rect.top - GAP, GAP, Math.max(GAP, window.innerHeight - CARD_HEIGHT - GAP))
  };
};

/**
 * A 64px (96px with `large`) product thumbnail that, on hover, floats a 320px preview card beside itself.
 * @param {string|null} source — image URL; without one the thumb is a placeholder and never previews
 */
const PreviewThumb = ({ source, title, subtitle, large }) => {
  const thumbRef = useRef(null);
  const timerRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  const open = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setAnchor(getCardAnchor(thumbRef.current.getBoundingClientRect())), OPEN_DELAY_MS);
  };
  const close = () => {
    clearTimeout(timerRef.current);
    setAnchor(null);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (!anchor) return undefined;
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [anchor]);

  return (
    <div ref={thumbRef} className={cn('silent-eval-preview-thumb', { large })} onMouseEnter={source ? open : undefined} onMouseLeave={close}>
      <SafeImage source={source} placeholderText='' alt='' />
      {anchor &&
        createPortal(
          <div className={cn('silent-eval-preview-card', anchor.side)} style={{ top: anchor.top, left: anchor.left }} role='tooltip'>
            <div className='preview-image'>
              <SafeImage source={source} placeholderText='' alt='' />
            </div>
            {(title || subtitle) && (
              <div className='preview-text'>
                {title && <div className='preview-title'>{title}</div>}
                {subtitle && <div className='preview-subtitle'>{subtitle}</div>}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

PreviewThumb.propTypes = {
  source: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  large: PropTypes.bool
};

export default PreviewThumb;
